import { isConditionComplete, isLegacyCondition } from "./conditions";
import { LOGIC, VACANCY_FILTER_KEY, type VacancyFilterKey } from "./constants";
import {
  hasLogic,
  logicSchema,
  operatorSchema,
  type CountPayload,
  type CountRulePayload,
  type FormCondition,
  type FormRule,
  type SegmentFormValues,
} from "./schemas";

import type {
  RuleCondition,
  SaveCondition,
  SaveRule,
  SaveSegmentRequest,
  SegmentDetailResponse,
  SegmentField,
} from "@/features/segments/types";
import i18n from "@/i18n";
import type { MightError } from "@/types/MightError";

const DEFAULT_SEGMENT_LOGIC = LOGIC.AND;
const DEFAULT_RULE_LOGIC = LOGIC.OR;

export const createEmptyCondition = (): FormCondition => ({
  clientKey: crypto.randomUUID(),
  field: "",
  values: [],
  isInverted: false,
});

export const createEmptyRule = (): FormRule => ({
  clientKey: crypto.randomUUID(),
  name: i18n.t("admin.segments.builder.rule.label"),
  type: DEFAULT_RULE_LOGIC,
  isInverted: false,
  conditions: [createEmptyCondition()],
});

export const createEmptySegmentValues = (): SegmentFormValues => ({
  name: "",
  type: DEFAULT_SEGMENT_LOGIC,
  isInverted: false,
  rules: [],
  vacancyOverrides: [],
});

const byIndex = <T extends { index: number }>(items: T[]): T[] =>
  [...items].sort((a, b) => a.index - b.index);

const withIndexes = <T extends { index?: number }>(
  items: T[],
): (T & { index: number })[] => {
  let nextIndex = Math.max(-1, ...items.map((item) => item.index ?? -1)) + 1;
  return items.map((item) => ({ ...item, index: item.index ?? nextIndex++ }));
};

const readStoredValues = ({ value, values }: RuleCondition): string[] =>
  values ?? (value ? [value] : []);

export const toFormValues = (
  segment: SegmentDetailResponse,
): SegmentFormValues => ({
  name: segment.name,
  type: logicSchema.optional().catch(undefined).parse(segment.type),
  isInverted: segment.isInverted,
  rules: byIndex(segment.rules).map((rule) => ({
    clientKey: rule.id,
    id: rule.id,
    index: rule.index,
    name: rule.name,
    type: logicSchema.optional().catch(undefined).parse(rule.type),
    isInverted: rule.isInverted,
    conditions: byIndex(rule.conditions).map((condition) => ({
      clientKey: condition.id,
      id: condition.id,
      index: condition.index,
      field: condition.field,
      operator: operatorSchema
        .optional()
        .catch(undefined)
        .parse(condition.operator),
      values: readStoredValues(condition),
      isInverted: condition.isInverted,
    })),
  })),
  vacancyOverrides: segment.vacancyOverrides.map(({ vacancyId, type }) => ({
    vacancyId,
    type,
  })),
});

const toSaveValues = ({
  values,
  isValueEdited,
}: FormCondition): FormCondition["values"] =>
  isValueEdited ? values.map((value) => value.trim()) : values;

const toSaveCondition = (
  condition: FormCondition & { index: number },
  fields: SegmentField[],
): SaveCondition | undefined => {
  const { operator } = condition;
  if (!operator) return undefined;
  if (!isLegacyCondition(condition, fields) && !isConditionComplete(condition))
    return undefined;
  return {
    id: condition.id,
    field: condition.field,
    operator,
    values: toSaveValues(condition),
    isInverted: condition.isInverted,
    index: condition.index,
  };
};

const toSaveConditions = (
  conditions: FormCondition[],
  ruleIndex: number,
  fields: SegmentField[],
): MightError<SaveCondition[], Error> => {
  const saved: SaveCondition[] = [];
  for (const [conditionIndex, condition] of withIndexes(conditions).entries()) {
    const savedCondition = toSaveCondition(condition, fields);
    if (!savedCondition) {
      return {
        data: null,
        error: new Error(
          `rule ${ruleIndex} condition ${conditionIndex} is incomplete`,
        ),
      };
    }
    saved.push(savedCondition);
  }
  return { data: saved, error: null };
};

const toSaveRules = (
  rules: SegmentFormValues["rules"],
  fields: SegmentField[],
): MightError<SaveRule[], Error> => {
  const saved: SaveRule[] = [];
  for (const [ruleIndex, rule] of withIndexes(rules).entries()) {
    if (!hasLogic(rule)) {
      return {
        data: null,
        error: new Error(`rule ${ruleIndex} has no logic type`),
      };
    }
    const { data: conditions, error } = toSaveConditions(
      rule.conditions,
      ruleIndex,
      fields,
    );
    if (error) return { data: null, error };
    saved.push({
      id: rule.id,
      name: rule.name,
      type: rule.type,
      isInverted: rule.isInverted,
      index: rule.index,
      conditions,
    });
  }
  return { data: saved, error: null };
};

export const toSaveRequest = (
  values: SegmentFormValues,
  clientId: string | undefined,
  fields: SegmentField[],
): MightError<SaveSegmentRequest, Error> => {
  if (!hasLogic(values))
    return { data: null, error: new Error("segment has no logic type") };
  const { data: rules, error } = toSaveRules(values.rules, fields);
  if (error) return { data: null, error };
  return {
    data: {
      clientId2: clientId,
      name: values.name,
      type: values.type,
      isInverted: values.isInverted,
      rules,
      vacancyOverrides: values.vacancyOverrides,
    },
    error: null,
  };
};

export const isRuleCountable = (rule: CountRulePayload): boolean =>
  rule.type !== undefined && rule.conditions.length > 0;

export const isSegmentCountable = (payload: CountPayload): boolean =>
  payload.type !== undefined && payload.rules.every(isRuleCountable);

export const toCountPayload = ({
  type,
  isInverted,
  rules,
}: Pick<SegmentFormValues, "type" | "isInverted" | "rules">): CountPayload => ({
  type,
  isInverted,
  rules: rules.map((rule) => ({
    type: rule.type,
    isInverted: rule.isInverted,
    conditions: rule.conditions.flatMap((condition) =>
      isConditionComplete(condition)
        ? [
          {
            field: condition.field,
            operator: condition.operator,
            values: condition.values,
            isInverted: condition.isInverted,
          },
        ]
        : [],
    ),
  })),
});

export const toSegmentCountBody = ({
  rules,
  ...payload
}: CountPayload): CountPayload => ({
  ...payload,
  rules: rules.filter((rule) => rule.conditions.length > 0),
});

export const toDraftBody = (
  values: Pick<
    SegmentFormValues,
    "type" | "isInverted" | "rules" | "vacancyOverrides"
  >,
  clientId: string | undefined,
) => ({
  ...toSegmentCountBody(toCountPayload(values)),
  clientId2: clientId,
  vacancyOverrides: values.vacancyOverrides,
});

export type DraftBody = ReturnType<typeof toDraftBody>;

export type DraftRules = Omit<DraftBody, "vacancyOverrides">;

export const toDraftRules = ({
  type,
  isInverted,
  rules,
  clientId2,
}: DraftBody): DraftRules => ({
  type,
  isInverted,
  rules,
  clientId2,
});

export const toVacancyFilterBody = (
  filters: Partial<Record<VacancyFilterKey, string[]>>,
) => ({
  overrideFilters: filters[VACANCY_FILTER_KEY.OVERRIDE],
  fields: filters[VACANCY_FILTER_KEY.FIELD],
  cities: filters[VACANCY_FILTER_KEY.CITY],
  companies: filters[VACANCY_FILTER_KEY.COMPANY],
});
